import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User, AuthTokens } from '@/types/auth.types'

interface AuthState {
  // State
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Computed
  isAuthenticated: boolean

  // Actions
  setUser: (user: User | null) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  login: (user: User, tokens: AuthTokens) => void
  logout: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  clearError: () => void
  refreshAuthToken: () => Promise<boolean>
  initializeAuth: () => Promise<void>
}

// Lazy import authService to avoid circular dependency
const getAuthService = async () => {
  const { authService } = await import('@/services/authService')
  return authService
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Computed - implemented as a derived property that gets recalculated
      // Note: We use a regular property here because getters cause issues during zustand persist hydration
      isAuthenticated: false,

      // Actions
      setUser: (user) => set({ user }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      login: (user, tokens) =>
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        }),

      logout: async () => {
        try {
          // Call logout API to invalidate tokens on server (send the refresh
          // token so it is revoked, not just the access token).
          const authService = await getAuthService()
          await authService.logout(get().refreshToken)
        } catch {
          // Ignore logout errors - continue with local cleanup
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      clearError: () => set({ error: null }),

      // Refresh the access token using the refresh token
      refreshAuthToken: async () => {
        const { refreshToken } = get()

        if (!refreshToken) {
          return false
        }

        try {
          const authService = await getAuthService()
          const tokens = await authService.refreshToken(refreshToken)
          set({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          })
          return true
        } catch {
          // If refresh fails, logout the user
          await get().logout()
          return false
        }
      },

      // Initialize auth state on app load
      initializeAuth: async () => {
        const { accessToken, refreshToken, isInitialized } = get()

        // Skip if already initialized
        if (isInitialized) {
          return
        }

        set({ isLoading: true })

        try {
          const authService = await getAuthService()

          // If we have an access token, try to get current user
          if (accessToken) {
            try {
              const user = await authService.getCurrentUser()
              set({ user, isAuthenticated: true, isLoading: false, isInitialized: true })
              return
            } catch {
              // Access token might be expired, try to refresh
            }
          }

          // If we have a refresh token, try to refresh the access token
          if (refreshToken) {
            const refreshed = await get().refreshAuthToken()
            if (refreshed) {
              // After refresh, get the current user
              try {
                const user = await authService.getCurrentUser()
                set({ user, isAuthenticated: true, isLoading: false, isInitialized: true })
                return
              } catch {
                // If getting user fails after refresh, logout
                await get().logout()
              }
            }
          }

          // No valid tokens, mark as initialized but not authenticated
          set({ isAuthenticated: false, isLoading: false, isInitialized: true })
        } catch {
          // On any error, clear auth state and mark as initialized
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            error: null,
          })
        }
      },
    }),
    {
      name: 'projectpulse-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (_state, error) => {
        // After rehydrating from localStorage, initialize auth
        if (error) {
          console.error('Error rehydrating auth state:', error)
        }
        // Use setTimeout to ensure the store is fully initialized before accessing it
        setTimeout(() => {
          // Set isAuthenticated based on restored state
          const currentState = useAuthStore.getState()
          const isAuthenticated = !!(currentState.accessToken && currentState.user)
          useAuthStore.setState({ isAuthenticated })
          // Initialize auth
          useAuthStore.getState().initializeAuth()
        }, 0)
      },
    }
  )
)

// Selector hooks for better performance
export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useAuthLoading = () => useAuthStore((state) => state.isLoading)
export const useAuthError = () => useAuthStore((state) => state.error)
export const useIsInitialized = () => useAuthStore((state) => state.isInitialized)

// Export type for external use
export type { AuthState }
